import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const gtcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const gtcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const gtcPrograms = [{
  slug: 'diploma-business-management',
  title: 'Diploma in Business Management',
  shortTitle: 'Business Management',
  mProgram: '63926'
}, {
  slug: 'diploma-computer-science',
  title: 'Diploma in Computer Science',
  shortTitle: 'Computer Science',
  mProgram: '63928'
}, {
  slug: 'aab-business-management',
  title: 'Associate of Applied Business (A.A.B.) in Business Management',
  shortTitle: 'A.A.B. Business Management',
  mProgram: '63927'
}, {
  slug: 'aas-computer-science',
  title: 'Associate of Applied Science (A.A.S.) in Computer Science',
  shortTitle: 'A.A.S. Computer Science',
  mProgram: '63929'
}];
export function GTCLandingPage() {
  usePageMeta('Global Tech College | Career-Focused Degree Programs', "Explore online degree programs at Global Tech College. Career-focused degree and certificate programs designed to prepare students for success in today's competitive job market.", {
    ogImage: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = gtcPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7mchg3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Global Tech College',
            logo: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
            primaryColor: '#1B365D',
            accentColor: '#8B1A1A',
            backUrl: '/colleges/global-tech-college',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const currentProgramTitle = gtcPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1758" data-mp="16h5y1ugl1akm">
          <div data-id="element-1759" data-mp="shwubu9kc8xj">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1760" data-mp="si6hzb74y402">
              Select a Program
            </label>
            <div className="relative" data-id="element-1761" data-mp="3d85emoiqcqnd">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#1B365D] focus:border-[#1B365D] font-medium text-gray-800" data-id="element-1762" data-mp="3d72nqgleqo7g">
                <option value="" data-id="element-1763" data-mp="7sjs8hri826j">-- Choose a Program --</option>
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1764" data-mp="3qpv2jgipgb4">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1765" data-mp="3uyu8mbitnvid">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1766" data-mp="vhm75hur7gxc" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-1767" data-mp="riwz84shirl3">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1768" data-mp="hq7ml6l777o6">
          <div data-id="element-1769" data-mp="hp6bj246ml6g">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1770" data-mp="1f64tzssaei8v">
              Program
            </label>
            <div className="relative" data-id="element-1771" data-mp="1adle2p64nb0w">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1772" data-mp="1hrcj4xrmsbkv">
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1773" data-mp="1rr3cgw4ggkjd">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1774" data-mp="20q70meihbq4q">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1775" data-mp="25nqcx8vm9su1" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1776" data-mp="2agt8fmjyfccv" />

          <div data-id="element-1777" data-mp="2febkhlt9q7p3">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1778" data-mp="2ff37474w0v38">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1779" data-mp="2oezvs8ra1tya">
              <option value="" data-id="element-1780" data-mp="22q3v9s5yuc4r">-- Please Select --</option>
              {gtcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1781" data-mp="27pqag75cu2g7">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1782" data-mp="2chhudyu099xd">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1783" data-mp="2lglb4w5p9d8p">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1784" data-mp="2lgbs5sj2o6ci">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1785" data-mp="2ver9x4w7m39c">
              <option value="" data-id="element-1786" data-mp="w400udbjdevr">-- Please Select --</option>
              {gtcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1787" data-mp="1119w49llwkri">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1788" data-mp="39kb4uijwxcdb">{errors.startDate}</p>}
          </div>

          <div data-id="element-1789" data-mp="35ig71o16xh4a">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1790" data-mp="35k117cjley1m">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1791" data-mp="159jjdf9nwm0">
              <option value="" data-id="element-1792" data-mp="3t6pneafmxco1">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1793" data-mp="3o7mjitbe5chn">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1794" data-mp="2cu0tz71md8vl">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1795" data-mp="27v7fpceiddud">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1796" data-mp="27uft2r2w2qg8">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1797" data-mp="2e9d71k1ph9cl" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1798" data-mp="20dspot1mkz7a">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1799" data-mp="1hm8b4uyburcv">
          <div data-id="element-1800" data-mp="1hn1hvvxadgv0">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1801" data-mp="1hjdqp3fbd6jg">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1802" data-mp="17oc4afcfaikk" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1803" data-mp="12z9l94f4c509">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1804" data-mp="xydgypquugub">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1805" data-mp="1byo8luzznzgj" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1806" data-mp="1lozp0h0v014b">
                <div data-id="element-1807" data-mp="1log8n4quxkse">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1808" data-mp="1lr2catamng5c">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="City" data-id="element-1809" data-mp="1uq7cs55r1jk5" />
                </div>
                <div data-id="element-1810" data-mp="3p4bd5ufwrr9y">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1811" data-mp="3p3jjip5vg3mb">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="State" data-id="element-1812" data-mp="2przjzyrcg4j" />
                </div>
              </div>
              <div data-id="element-1813" data-mp="3f7qavgv9wu9t">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1814" data-mp="3f884e5qjwue1">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`} placeholder="123 Main St" data-id="element-1815" data-mp="uhhevpcquo45" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1816" data-mp="218wu8k2lkn94">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1817" data-mp="1wa3f6n7nwo6u">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1818" data-mp="1w6o03kdznf5f">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1819" data-mp="1i9ss7v9h3tqy" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1820" data-mp="3hmwslqlso4u9">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1821" data-mp="4fc7r4tjb91q">
          <div className="grid grid-cols-2 gap-3" data-id="element-1822" data-mp="3mrgzs7fmt6c6">
            <div data-id="element-1823" data-mp="3mt1z0acw5dgd">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1824" data-mp="3mpe2d2c5gaxf">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1825" data-mp="jb06ieg333y8" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1826" data-mp="9ef1vu7z7uux">{errors.firstName}</p>}
            </div>
            <div data-id="element-1827" data-mp="edi99lbfid62">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1828" data-mp="2u2jkmd6pvpnn">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1829" data-mp="2z2xum0rvp9ob" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1830" data-mp="2mldbdwt2v0h1">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1831" data-mp="2vjpbcpfu3am5">
            <div data-id="element-1832" data-mp="2vl0csilt46a3">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1833" data-mp="2vki7kwh62hq1">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1834" data-mp="23tkgtxyd1777" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1835" data-mp="2cro4azswoe1n">{errors.email}</p>}
            </div>
            <div data-id="element-1836" data-mp="1p2bx25h52g1z">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1837" data-mp="1p1ttens5czk1">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1838" data-mp="zjrwhuhbxct9" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1839" data-mp="18lhl6sjttxnc">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D] cursor-pointer flex-shrink-0" data-id="element-1840" data-mp="1dg50q1wurhps">
            By clicking the Request Info button, you agree to be contacted by
            phone, text message, or email via automated systems by Global Tech
            College about your education at the phone numbers you provided
            above, including any wireless number(s). You are not obligated to
            agree to automated contact to enroll. Note that even non-automated
            calls are recorded for quality assurance.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1841" data-mp="3lpv1euip3vnb">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1842" data-mp="3lotgzzfshwsx">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1843" data-mp="da1k4dgjig57" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#8B1A1A] to-[#A52A2A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1844" data-mp="8bhy6d1y54cu">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1845" data-mp="w2ne0s7vzwn5" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1846" data-mp="2hsk422jv21eg">
      {/* A. Custom Header */}
      <header className="bg-white py-2 sm:py-4 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1847" data-mp="2cy6a84kuk2bw">
        <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt="Global Tech College Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-1848" data-mp="23v59ykinx5g5" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-br from-[#1B365D] to-[#8B1A1A] relative overflow-hidden" data-id="element-1849" data-mp="2wjkjwsmxsxkm">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1850" data-mp="2rsaxe0woqacy" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-1851" data-mp="3rtz9511dvojz">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-1852" data-mp="4nykazareafy">
            <p className="text-[#D4A843] font-medium mb-3 text-lg tracking-wide" data-id="element-1853" data-mp="9m9vbdcsiqzr">
              Global Tech College Online<sup className="text-xs" data-id="element-1854" data-mp="el3791cfi9xt">1</sup>
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-1855" data-mp="jdcw0e1t53yi">
              {gtcPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Career-Focused
                  <br data-id="element-1856" data-mp="sf3vv29o2gkg" />
                  Degree Programs
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-1857" data-mp="xaj8jqvxu6fa">
            <form onSubmit={e => e.preventDefault()} id="gtc-lead-form" data-id="element-1858" data-mp="336d9hvv1fqi0">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1859" data-mp="2ye296rwdy2ox" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1860" data-mp="3d2yi0r69njql" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1861" data-mp="385pwcglwsxkl">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1862" data-mp="3h537ddpolu7f">
                <div className="bg-gradient-to-r from-[#1B365D] to-[#2A528A] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1863" data-mp="1rqeu7a99o88c"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1864" data-mp="2akm9ntg6n0g">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1865" data-mp="2bm88nc04twy">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-1866" data-mp="3ap2ze3y8wsqx">
            Global Tech College offers career-focused degree and certificate
            programs designed to prepare students for success in today's
            competitive job market. With flexible online options and dedicated
            support, we help you achieve your educational goals.
          </p>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-gray-50 py-12 border-b border-gray-200" data-id="element-1867" data-mp="a7kzhtirw8g9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1868" data-mp="5auldbqj3mxh">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1869" data-mp="5cfi980bfhyi">
            <div className="flex flex-col items-center text-center" data-id="element-1870" data-mp="1a0victizy1y">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1871" data-mp="3smvnmvbx27vx">
                <LaptopIcon className="w-8 h-8" data-id="element-1872" data-mp="3nok947aof97d" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1873" data-mp="3ipqx6jb1fq9b">
                100% Online Flexibility
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1874" data-mp="3dpcyhrvbtsny">
                Study on your schedule with 24/7 access to coursework.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1875" data-mp="1p6rxnpcofnrk">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1876" data-mp="1p8l5v92xug1o">
                <BriefcaseIcon className="w-8 h-8" data-id="element-1877" data-mp="1y76of48etv2g" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1878" data-mp="1y53yslb663y9">
                Career-Focused Curriculum
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1879" data-mp="15irkpduqaz6f">
                Gain practical skills designed for today's job market.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1880" data-mp="1af9n472l6lfl">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1881" data-mp="1ffnk8u2ngayu">
                <AwardIcon className="w-8 h-8" data-id="element-1882" data-mp="1k8gs3qtezzf5" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1883" data-mp="mqqaxggf1oi8">
                Accredited Institution
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1884" data-mp="rp0cc8sqhlca">
                Accredited by ACCET, ensuring quality education standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Section */}
      <section className="py-16 bg-white" data-id="element-1885" data-mp="tfowqlf84jge">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1886" data-mp="tfgjgsf6vc4o">
          <h2 className="text-3xl font-bold text-[#1B365D] mb-6" data-id="element-1887" data-mp="ohwuaxgydexy">
            Why Choose Global Tech College?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1888" data-mp="rt17k1e4xl7">
            At Global Tech College, we are committed to providing accessible,
            high-quality education that empowers students to achieve their
            professional goals. Our programs are designed with input from
            industry experts to ensure you learn the skills employers are
            looking for right now.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 text-left border border-gray-100" data-id="element-1889" data-mp="1m6qgkztv3369">
            <h3 className="text-xl font-bold text-[#1B365D] mb-4" data-id="element-1890" data-mp="1h5axvef5p8r1">
              Student Support Services
            </h3>
            <ul className="space-y-3" data-id="element-1891" data-mp="3428zw5zt1xwx">
              <li className="flex items-start" data-id="element-1892" data-mp="343bp89hhbsaq">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1893" data-mp="33zw8l285qb77" />
                <span className="text-gray-700" data-id="element-1894" data-mp="2q4kbwpafyann">
                  Live Tutor support via email & chat
                </span>
              </li>
              <li className="flex items-start" data-id="element-1895" data-mp="3iqwt45m6hvjp">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1896" data-mp="3it9hp1mfz45q" />
                <span className="text-gray-700" data-id="element-1897" data-mp="3muuqobvuuyan">
                  Dedicated academic advisors to guide your journey
                </span>
              </li>
              <li className="flex items-start" data-id="element-1898" data-mp="3dzeptawonivk">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1899" data-mp="3dy3n7eez4h61" />
                <span className="text-gray-700" data-id="element-1900" data-mp="5ik0yzcpzz0h">
                  Career services assistance including resume building and
                  interview prep
                </span>
              </li>
              <li className="flex items-start" data-id="element-1901" data-mp="26gkphtjxbsuh">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1902" data-mp="21nhzfvecv1j1" />
                <span className="text-gray-700" data-id="element-1903" data-mp="1wnwzbhng8n4q">
                  Flexible learning with pre-recorded and live lectures
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* E. Footer */}
      <footer className="bg-[#1B365D] text-white/70 py-12 text-xs" data-id="element-1904" data-mp="2pb9j5k286guc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4" data-id="element-1905" data-mp="2kfkhnb6napve">
          <p data-id="element-1906" data-mp="2fe4w7i0k2hch">
            Global Tech College is accredited by The Accrediting Council for
            Continuing Education & Training (ACCET).
          </p>
          <p data-id="element-1907" data-mp="14012mcxvqv7y">
            <sup className="text-[10px]" data-id="element-1908" data-mp="13zre6tshgw4d">1</sup> Online programs are subject to
            state availability. Not all programs are available in all states.
          </p>
          <div className="pt-4 border-t border-white/10 space-y-3" data-id="element-1909" data-mp="100ifdail681y">
            <LegalLinks align="center" className="[&_a]:text-white/70 [&_a:hover]:text-white" data-id="element-1910" data-mp="3bn2l5k0994ml" />
            <p data-id="element-1911" data-mp="3fl06gbqwn6kp">
              © {new Date().getFullYear()} Global Tech College. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}